// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createArtifact } from '../services/sopFactoryService';
import { fromPolicyDetail } from './fromPolicyDetail';
import { toPolicyDetail } from './toPolicyDetail';
import type { PolicyDetail } from '../types/procedures';

export interface CreatePolicyView {
  create: (policy: PolicyDetail) => Promise<PolicyDetail>;
  isCreating: boolean;
  error: string | null;
}

/**
 * Policy-as-code counterpart of `useCreateProcedure` — same
 * `createArtifact()` relay and cache invalidation, wrapping the
 * `PolicyDetail`<->`Artifact` mapping instead.
 */
export function useCreatePolicy(): CreatePolicyView {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (policy: PolicyDetail) => {
      const payload = fromPolicyDetail(policy, {});
      const created = await createArtifact(payload);
      return toPolicyDetail(created);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-center'] });
    },
  });

  return {
    create: policy => mutation.mutateAsync(policy),
    isCreating: mutation.isPending,
    error: mutation.isError ? 'Unable to create this policy.' : null,
  };
}
